/**
 * Internal sub-components for MappingPhase.
 * Extracted to keep mapping-phase.tsx under the ESLint max-lines-per-function limit.
 */
import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Label } from '@ooh/ui/components/label';
import { LabeledSearchableSelectField } from '@ooh/ui/components/labeled-searchable-select-field';
import type { SearchableSelectFieldOption } from '@ooh/ui/components/searchable-select-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ooh/ui/components/select';
import { MediaTypeEnum } from '@ooh/contracts';
import type {
  FieldOptionItem,
  MappingPhaseLabels,
  SupplierOption,
  ColumnMappingItem,
} from './mapping-phase';
import { isSupplierFieldCovered, isMediaTypeFieldCovered } from '../lib/collect-mapped-field-keys';

/** Sentinel value used by the Select to represent "skip this column" */
export const SKIP_VALUE = '__skip__' as const;

/** Canonical column name for the media type column in the supplier file. */
export const TYP_NOSNIKA_SOURCE_COLUMN = 'Typ nośnika';

/** Returns required fields that have a source column in the file but are currently unmapped. */
export function getUnmappedRequiredFields(
  fieldOptions: FieldOptionItem[],
  mappedSystemFieldKeys: Set<string>,
  missingRequiredFieldKeys: string[] | undefined,
  supplierCoveredByCombobox: boolean,
  mediaTypeOverride: string | undefined,
): FieldOptionItem[] {
  return fieldOptions.filter(
    (o) =>
      o.required === true &&
      !mappedSystemFieldKeys.has(o.key) &&
      !(missingRequiredFieldKeys ?? []).includes(o.key) &&
      !isSupplierFieldCovered(o.key, supplierCoveredByCombobox) &&
      !isMediaTypeFieldCovered(o.key, mediaTypeOverride),
  );
}

/** Applies a single mapping change to the mapping list. */
export function applyMappingChange(
  prev: ColumnMappingItem[],
  id: string,
  value: string,
  skipValue: string,
): ColumnMappingItem[] {
  return prev.map((m) =>
    m.id === id ? { ...m, mappedTo: value === skipValue ? null : value } : m,
  );
}

interface MappingStatusBannerProps {
  nonStandardBanner: string;
}

export function MappingStatusBanner({ nonStandardBanner }: MappingStatusBannerProps) {
  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200"
      data-testid="mapping-fallback-banner"
    >
      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
      <p className="text-sm text-amber-800 font-medium">{nonStandardBanner}</p>
    </div>
  );
}

interface MappingErrorBannerProps {
  errorMessage: string | null | undefined;
}

export function MappingErrorBanner({ errorMessage }: MappingErrorBannerProps) {
  if (!errorMessage) return null;
  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200"
      data-testid="mapping-error-banner"
    >
      <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
      <p className="text-sm text-red-800 font-medium">{errorMessage}</p>
    </div>
  );
}

export interface RequiredFieldsErrorSectionProps {
  icon: React.ReactNode;
  heading: string;
  hint: string;
  fields: FieldOptionItem[];
  testId: string;
  chipTestIdPrefix: string;
}

export function RequiredFieldsErrorSection({
  icon,
  heading,
  hint,
  fields,
  testId,
  chipTestIdPrefix,
}: RequiredFieldsErrorSectionProps) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2" data-testid={testId}>
      <div className="flex items-start gap-2">
        {icon}
        <div className="space-y-1">
          <p className="text-sm font-semibold text-red-800">{heading}</p>
          <p className="text-xs text-red-700">{hint}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 pl-6">
        {fields.map((f) => (
          <span
            key={f.key}
            className="inline-flex items-center rounded-md border border-red-300 bg-red-100 px-2 py-0.5 text-2xs font-medium text-red-700"
            data-testid={`${chipTestIdPrefix}-${f.key}`}
          >
            {f.label}
          </span>
        ))}
      </div>
    </div>
  );
}

interface MappingSupplierSelectProps {
  suppliers: SupplierOption[];
  selectedSupplierId: string | null;
  onSupplierSelect: (id: string | null) => void;
  labels: Pick<
    MappingPhaseLabels,
    | 'supplierLabel'
    | 'supplierSelectPlaceholder'
    | 'supplierSearchPlaceholder'
    | 'supplierNoResults'
  >;
}

/**
 * Always-visible top-of-form supplier select — reuses the availability-import
 * SupplierMappingFields pattern, now backed by `LabeledSearchableSelectField`
 * instead of a plain `LabeledSelectField` (supplier lists are unbounded, per
 * `packages/ui/AGENTS.md`'s "reserve plain Select for ~5 or fewer options" rule)
 * — not a checkbox + combobox pair (column-mapping-ui reposition: the supplier
 * picker moved from the bottom of the mapping form to the top, mirroring the
 * sibling availability-import feature's single always-visible supplier
 * selector instead of a "remember mapping" toggle).
 */
export function MappingSupplierSelect({
  suppliers,
  selectedSupplierId,
  onSupplierSelect,
  labels,
}: MappingSupplierSelectProps) {
  const options: SearchableSelectFieldOption[] = suppliers.map((s) => ({
    value: s.id,
    label: s.name,
  }));
  return (
    <div data-testid="mapping-supplier-select-section">
      <LabeledSearchableSelectField
        id="mapping-supplier-select"
        label={labels.supplierLabel}
        options={options}
        value={selectedSupplierId ?? ''}
        onChange={(next) => onSupplierSelect(next === '' ? null : next)}
        placeholder={labels.supplierSelectPlaceholder}
        searchPlaceholder={labels.supplierSearchPlaceholder}
        emptyText={labels.supplierNoResults}
        testId="mapping-supplier-select"
      />
    </div>
  );
}

/** Sentinel value for "no override" — per-row dispatch */
export const DEFAULT_OVERRIDE_VALUE = '__default__' as const;

interface MediaTypeOverridePickerProps {
  /** Current media type override value, or undefined for no override */
  mediaTypeOverride?: string;
  /** Called with a media type string when override is picked, or null to clear */
  onChange: (value: string | null) => void;
  labels: Pick<MappingPhaseLabels, 'typeOverrideLabel' | 'typeOverridePlaceholder'>;
}

/**
 * Converts a raw Select value to the override value expected by the parent.
 * When the sentinel DEFAULT_OVERRIDE_VALUE is selected ("no override"), returns null.
 */
export function handleMediaTypeValueChange(
  value: string,
  onChange: (v: string | null) => void,
): void {
  onChange(value === DEFAULT_OVERRIDE_VALUE ? null : value);
}

export function MediaTypeOverridePicker({
  mediaTypeOverride,
  onChange,
  labels,
}: MediaTypeOverridePickerProps) {
  const currentValue = mediaTypeOverride ?? DEFAULT_OVERRIDE_VALUE;

  function handleValueChange(value: string) {
    handleMediaTypeValueChange(value, onChange);
  }

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/20"
      data-testid="media-type-override-picker"
    >
      <Label
        htmlFor="media-type-override-select"
        className="text-sm font-medium text-foreground shrink-0"
      >
        {labels.typeOverrideLabel}
      </Label>
      <Select value={currentValue} onValueChange={handleValueChange}>
        <SelectTrigger
          id="media-type-override-select"
          className="h-8 text-sm min-w-44"
          data-testid="media-type-override-trigger"
        >
          <SelectValue placeholder={labels.typeOverridePlaceholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={DEFAULT_OVERRIDE_VALUE}>{labels.typeOverridePlaceholder}</SelectItem>
          {MediaTypeEnum.options.map((type) => (
            <SelectItem key={type} value={type} data-testid={`media-type-option-${type}`}>
              {type}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
